import { Component, HostBinding, Input, OnInit, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { JsonValue } from '@bufbuild/protobuf';
import { create, fromJson } from '@bufbuild/protobuf';
import { ConnectError } from '@connectrpc/connect';
import { NbToastrService, NbWindowRef, NbWindowService } from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormFieldAutocomplete } from '@vmspdb/components/form-fields/autocomplete';
import { FormFieldSelectAutocomplete } from '@vmspdb/components/form-fields/select-autocomplete';
import {
  TimeTrackingDefaultType,
  TimeTrackingDefaultTypeSchema,
  TimeTrackingServiceAllowedSurveyOptionsRequestSchema,
  TimeTrackingServiceCreateRequestJson,
  TimeTrackingServiceCreateRequestSchema,
  TimeTrackingServiceCreateResponseJson,
  enumEquals,
  enumName,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { getEnumOptions } from '@vmspdb/ui/domain';
import { selectAuthUserId } from '@vmspdb/ui/store';
import {
  distinctUntilChanged,
  first,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import {
  ProjectJobRepositoryService,
  TimeTrackingRepositoryService,
  handleOptionsError,
} from '../../../repositories';
import { SearchService, projectProps } from '../../../services/search.service';
import { VOID, getFormErrors } from '../../../shared/utils';
import { selectMyTimeTracking } from '../../../store/time-tracking';
import { GlobalTimeTrackingOfficeModalComponent } from '../time-tracking-office-modal/global-time-tracking-office-modal.component';

@UntilDestroy()
@Component({
  selector: 'vmspdb-time-tracking-modal',
  templateUrl: './global-time-tracking-modal.component.html',
  styleUrls: ['./global-time-tracking-modal.component.scss'],
  standalone: false,
})
export class GlobalTimeTrackingModalComponent implements OnInit {
  @HostBinding('attr.data-testid') testid = 'modal-global-time-tracking';

  private store = inject(Store);
  private repository = inject(TimeTrackingRepositoryService);
  private toastr = inject(NbToastrService);
  private errorHandler = inject(GlobalErrorHandlerService);
  private projectJobs = inject(ProjectJobRepositoryService);
  private nbWindowRef = inject(NbWindowRef);
  private win = inject(NbWindowService);
  private searchService = inject(SearchService);

  form = new FormGroup({} as any);
  fields: FormlyFieldConfig[];

  @Input()
  model: TimeTrackingServiceCreateResponseJson = {};

  @Input()
  allowProjectUpdate = true;
  errors: string[] = [];
  switchingProjects = false;
  myTracking$ = this.store.select(selectMyTimeTracking);
  processing = false;

  private lastProjectUuid: string | undefined;

  constructor() {
    this.fields = this.getFieldConfig();
  }

  ngOnInit() {
    this.lastProjectUuid = this.model.projectUuid;
  }

  private getFieldConfig(): FormlyFieldConfig[] {
    return [
      {
        key: 'crewLeader',
        type: 'radio',
        props: {
          label: 'Crew Leader',
          required: true,
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
        },
      },
      {
        key: 'projectUuid',
        defaultValue: '',
        type: FormFieldAutocomplete,
        props: {
          label: 'Project',
          required: true,
          ...projectProps(this.searchService, false),
          change: () => {
            if (this.model.projectUuid === this.lastProjectUuid) {
              return;
            }
            this.lastProjectUuid = this.model.projectUuid;
            const model = { ...this.model };
            delete model['projectJobUuid'];
            delete model['projectSurveyTaskUuid'];
            this.model = model;
          },
        },
        expressions: {
          'props.disabled': () => !this.allowProjectUpdate,
        },
      },
      {
        key: 'projectSurveyTaskUuid',
        type: 'select',
        defaultValue: '',
        props: {
          label: 'Survey Task',
        },
        hooks: {
          onInit: (field) => {
            const projectControl =
              field.parent?.get?.('projectUuid')?.formControl;
            if (!(projectControl && field.props)) {
              return;
            }

            field.props.options = projectControl.valueChanges.pipe(
              startWith(projectControl.value),
              distinctUntilChanged(),
              map(() =>
                create(TimeTrackingServiceAllowedSurveyOptionsRequestSchema, {
                  projectUuid: this.model.projectUuid,
                  showAll: Boolean(this.model.uuid || this.allowProjectUpdate),
                }),
              ),
              switchMap((req) => {
                return req.projectUuid
                  ? this.repository.allowedSurveyOptions(req)
                  : of([
                      {
                        label: 'N/A',
                        value: '',
                      },
                    ]);
              }),
              handleOptionsError(this.errorHandler),
            );
          },
        },
        expressions: {
          hide: () =>
            Boolean(this.model.projectJobUuid || !this.model.projectUuid),
          'props.disabled': () => !this.allowProjectUpdate,
        },
      },
      {
        key: 'projectJobUuid',
        type: FormFieldSelectAutocomplete,
        props: {
          label: 'Job',
          required: true,
        },
        hooks: {
          onInit: (field) => {
            const projectControl =
              field.parent?.get?.('projectUuid')?.formControl;
            if (!(projectControl && field.props)) {
              return;
            }

            field.props.options = projectControl.valueChanges.pipe(
              startWith(projectControl.value),
              distinctUntilChanged(),
              map(() => this.model.projectUuid),
              switchMap((value) =>
                value ? this.projectJobs.getOptions(value.toString()) : of([]),
              ),
              handleOptionsError(this.errorHandler),
              shareReplay({ bufferSize: 1, refCount: false }),
            );
          },
        },
        expressions: {
          hide: () =>
            Boolean(
              !this.model.projectUuid || this.model.projectSurveyTaskUuid,
            ),
        },
      },
      {
        key: 'task.default',
        type: FormFieldSelectAutocomplete,
        defaultValue: enumName(
          TimeTrackingDefaultTypeSchema,
          TimeTrackingDefaultType.UNSPECIFIED,
        ),
        props: {
          label: 'Task',
          options: getEnumOptions(TimeTrackingDefaultTypeSchema, true),
          compareWith: (
            o1: TimeTrackingDefaultType,
            o2: TimeTrackingDefaultType,
          ) => enumEquals(TimeTrackingDefaultTypeSchema, o1, o2),
          required: true,
        },
        expressions: {
          hide: '!model.projectJobUuid',
        },
      },
      {
        key: 'task.custom.text',
        type: 'input',
        props: {
          label: 'Custom Task Description',
          required: true,
        },
        expressions: {
          hide: () => {
            return this.customTextHidden();
          },
        },
      },
    ];
  }

  finishTracking() {
    this.processing = true;
    return this.myTracking$.pipe(
      first(),
      untilDestroyed(this),
      switchMap((model) => {
        return this.repository
          .markComplete(model?.uuid ?? '')
          .pipe(untilDestroyed(this));
      }),
      tap(() => this.toastr.success(`Time tracking completed!`, 'Success')),
      tap(() => {
        this.switchingProjects = false;
        this.allowProjectUpdate = true;
        this.model = {};
      }),
      catchError((err) => {
        this.errorHandler.handleError(err);
        this.toastr.danger('There was an error saving your changes', 'Error');
        return VOID;
      }),
      tap(() => (this.processing = false)),
    );
  }

  saveChanges() {
    this.processing = true;
    this.errors = [];
    const model: TimeTrackingServiceCreateRequestJson = {
      ...this.model,
      inProgress: true,
      startTime: new Date().toISOString(),
      date: new Date().toISOString(),
    };

    return this.store.select(selectAuthUserId).pipe(
      first(),
      switchMap((employeeUuid) =>
        this.repository.create(
          fromJson(
            TimeTrackingServiceCreateRequestSchema,
            {
              ...model,
              employeeUuid,
            } as JsonValue,
            {
              ignoreUnknownFields: true,
            },
          ),
        ),
      ),
      tap(() => this.toastr.success(`Time tracking started!`, 'Success')),
      untilDestroyed(this),
      tap(() => this.nbWindowRef.close()),
      catchError((err: ConnectError) => {
        this.errorHandler.handleError(err);
        this.errors = [err.message];
        return VOID;
      }),
      tap(() => (this.processing = false)),
    );
  }

  startOffice() {
    this.win.open(GlobalTimeTrackingOfficeModalComponent, {
      title: 'Start Office Time Tracking',
      windowClass: 'modal-compact',
    });
    this.nbWindowRef.close();
  }

  submit() {
    if (this.switchingProjects) {
      this.finishTracking().subscribe();
      return;
    }

    this.saveChanges()
      .pipe(tap(() => this.nbWindowRef.close()))
      .subscribe();
  }

  clockOut() {
    this.finishTracking()
      .pipe(tap(() => this.nbWindowRef.close()))
      .subscribe();
  }

  get formErrors() {
    return getFormErrors(this.form);
  }

  private customTextHidden() {
    if (
      this.model.task?.default === undefined ||
      this.model.projectSurveyTaskUuid
    ) {
      return true;
    }

    return !enumEquals(
      TimeTrackingDefaultTypeSchema,
      TimeTrackingDefaultType.UNSPECIFIED,
      this.model.task?.default || '',
    );
  }

  get invalid() {
    // Force check
    const errors = this.formErrors;
    return this.form.invalid || errors?.['hullo'];
  }
}
